<template>
  <div class="w-[230px]">
    <SidbearSettingsGroup
      v-for="_type in types"
      :key="_type.value"
      :active="type === _type.value"
      :title="_type.label"
      button-class="d-flex"
      @opened="() => type = _type.value"
    >
      <template #title>
        <div class="flex items-center">
          <span>
            {{ _type.label }}
          </span>
          <i
            v-if="_type.desc"
            v-b-tooltip
            class="fa fa-info-circle cursor-pointer hover:opacity-80 ml-2"
            :title="_type.desc"
          />
        </div>
      </template>
      <div class="py-[14px]">
        <font-family-and-weight-control
          :global-style-option-enable="false"
          :family="fontFamily"
          :weight="fontWeight"
          @family="family => fontFamily = family"
          @weight="weight => fontWeight = weight"
        />

        <b-form-group
          v-if="_type.value !== 'link' && _type.value !== 'link-hover'"
          label="Font Size"
        >
          <div class="flex items-center gap-[6px]">
            <v-select
              v-model="fontSize"
              :options="fontSizes()"
              class="custom-select"
              left
            />

            <color-picker
              :color="color"
              @input="event => color = `rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`"
            />
          </div>
        </b-form-group>

        <b-form-group
          v-else
          label="Color"
        >
          <color-picker
            :color="color"
            @input="event => color = `rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`"
          />
        </b-form-group>

        <b-form-group v-if="!['link-hover','link'].includes(_type.value)">
          <div class="flex items-center justify-between">
            <label class="p-0">Text Alignment</label>
            <div
              class="flex items-center btn-group"
            >
              <button
                v-for="(option, index) in alignOptions"
                :key="index"
                v-b-tooltip.hover
                :class="{'!bg-[#F2F4F5]': align === (option.value)}"
                class="btn flex justify-center !transition items-center h-[29px] !px-[10px] py-0 text-[12px] !font-[600] border rounded-[4px] !text-[#44474A] hover:!bg-[#F2F4F5]"
                :title="option.tooltip"
                @click="align = option.value"
              >
                <img
                  v-if="option.svg"
                  :src="option.svg"
                  alt="icon"
                >
                <span
                  v-else
                  v-text="option.text"
                />
              </button>
            </div>
          </div>
        </b-form-group>

        <b-form-group
          v-if="_type.value !== 'link-hover'"
          label="Line Height"
          label-class="!pb-[3px]"
        >
          <b-row class="align-items-center pl-2">
            <b-col
              cols="8"
            >
              <vue-slider
                v-model="lineHeight"
                :interval=".1"
                :max="3.6"
                :min="1"
                class="editor-slider-1"
                tooltip="none"
              />
            </b-col>
            <b-col>
              <b-input
                v-model="lineHeight"
                :max="3.6"
                :min="1"
                :step=".1"
                size="sm"
                type="number"
              />
            </b-col>
          </b-row>
        </b-form-group>

        <b-form-group
          v-if="_type.value !== 'link-hover'"
          label="Letter Spacing"
          label-class="!pb-[3px]"
        >
          <b-row class="align-items-center pl-2">
            <b-col
              cols="8"
            >
              <vue-slider
                v-model="letterSpacing"
                :interval="1"
                :max="20"
                :min="0"
                class="editor-slider-1"
                tooltip="none"
              />
            </b-col>
            <b-col>
              <b-input
                v-model="letterSpacing"
                :max="20"
                :min="0"
                :step="1"
                size="sm"
                type="number"
              />
            </b-col>
          </b-row>
        </b-form-group>
      </div>
    </SidbearSettingsGroup>
  </div>
</template>

<script>
import _ from 'lodash'
import {mapState} from 'vuex'
import ResolutionMixin from '@/components/mixins/ResolutionMixin'
import SidbearSettingsGroup from '@/components/editor/components/SidbearSettingsGroup'
import FontFamilyAndWeightControl from '@/components/editor/controls/FontFamilyAndWeightControl'

export default {
  name: 'TextSettings',
  components: {FontFamilyAndWeightControl, SidbearSettingsGroup},
  mixins: [ResolutionMixin],

  data () {
    return {
      type: 'h2',
      types: [
        {
          label: 'H1 Heading',
          value: 'h1'
        },
        {
          label: 'H2 Heading',
          value: 'h2',
          desc:'(Headline Default value)'
        },
        {
          label: 'H3 Heading',
          value: 'h3',
          desc:'(Subheadline Default value)'
        },
        {
          label: 'H4 Heading',
          value: 'h4'
        },
        {
          label: 'H5 Heading',
          value: 'h5'
        },
        {
          label: 'H6 Heading',
          value: 'h6'
        },
        {
          label: 'Paragraph',
          value: 'paragraph'
        },
        {
          label: 'Link',
          value: 'link'
        },
        {
          label: 'Link Hover',
          value: 'link-hover'
        }
      ],
      alignOptions:[
        {
          svg: 'data:image/svg+xml,%3Csvg width=\'16\' height=\'16\' viewBox=\'0 0 16 16\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Crect x=\'0.475586\' y=\'0.475464\' width=\'14.9508\' height=\'14.9508\' fill=\'url(%23pattern0)\'/%3E%3Cdefs%3E%3Cpattern id=\'pattern0\' patternContentUnits=\'objectBoundingBox\' width=\'1\' height=\'1\'%3E%3Cuse xlink:href=\'%23image0_7786_57178\' transform=\'scale(0.00195312)\'/%3E%3C/pattern%3E%3Cimage id=\'image0_7786_57178\' width=\'512\' height=\'512\' xlink:href=\'data:image/png;base64,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\'/%3E%3C/defs%3E%3C/svg%3E%0A',
          value: 'left',
          tooltip: 'Left'
        },
        {
          svg: 'data:image/svg+xml,%3Csvg width=\'18\' height=\'14\' viewBox=\'0 0 18 14\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Crect x=\'0.951172\' y=\'0.431396\' width=\'16.5246\' height=\'13.0389\' fill=\'url(%23pattern0)\'/%3E%3Cdefs%3E%3Cpattern id=\'pattern0\' patternContentUnits=\'objectBoundingBox\' width=\'1\' height=\'1\'%3E%3Cuse xlink:href=\'%23image0_7786_57177\' transform=\'scale(0.00195312 0.00247525)\'/%3E%3C/pattern%3E%3Cimage id=\'image0_7786_57177\' width=\'512\' height=\'404\' xlink:href=\'data:image/png;base64,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\'/%3E%3C/defs%3E%3C/svg%3E%0A',
          value: 'center',
          tooltip: 'Center'
        },
        {
          svg: 'data:image/svg+xml,%3Csvg width=\'15\' height=\'16\' viewBox=\'0 0 15 16\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Crect x=\'14.9512\' y=\'15.4263\' width=\'14.9508\' height=\'14.9508\' transform=\'rotate(180 14.9512 15.4263)\' fill=\'url(%23pattern0)\'/%3E%3Cdefs%3E%3Cpattern id=\'pattern0\' patternContentUnits=\'objectBoundingBox\' width=\'1\' height=\'1\'%3E%3Cuse xlink:href=\'%23image0_7786_57179\' transform=\'scale(0.00195312)\'/%3E%3C/pattern%3E%3Cimage id=\'image0_7786_57179\' width=\'512\' height=\'512\' xlink:href=\'data:image/png;base64,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\'/%3E%3C/defs%3E%3C/svg%3E%0A',
          value: 'right',
          tooltip: 'Right'
        }
      ]
    }
  },

  computed: {
    ...mapState('editor', {
      globalStyles: state => state.globalStyles
    }),
    ...mapState('font', {
      fonts: state => state.fonts
    }),
    fontWeight: {
      get () {
        return this.textStyles.fontWeight[this.resolutioner]
      },
      set (val) {
        this.setValue('fontWeight', val)
      }
    },
    align: {
      get () {
        return this.textStyles.align[this.resolutioner]
      },
      set (val) {
        this.setValue('align', val)
      }
    },
    textDecoration: {
      get () {
        return this.textStyles.textDecoration[this.resolutioner]
      },
      set (val) {
        this.setValue('textDecoration', val)
      }
    },
    textStyle: {
      get () {
        return this.textStyles.textStyle[this.resolutioner]
      },
      set (val) {
        this.setValue('textStyle', val)
      }
    },
    lineHeight: {
      get () {
        return this.textStyles.lineHeight[this.resolutioner] || 1.3
      },
      set (val) {
        this.setValue('lineHeight', val)
      }
    },
    letterSpacing: {
      get () {
        return this.textStyles.letterSpacing[this.resolutioner]
      },
      set (val) {
        this.setValue('letterSpacing', val)
      }
    },
    color: {
      get () {
        return this.textStyles.color.lg
      },
      set (val) {
        this.textStyles.color.lg = val
      }
    },
    fontSize: {
      get () {
        return this.textStyles.fontSize[this.resolutioner]
      },
      set (val) {
        this.setValue('fontSize', val)
      }
    },
    fontFamily: {
      get () {
        return this.textStyles.fontFamily[this.resolutioner]
      },
      set (val) {
        this.setValue('fontFamily', val)
      }
    },
    textStyles () {
      if (!this.globalStyles.textStyles) {
        this.$set(this.globalStyles, 'textStyles', {
          'h1': {
            fontFamily: {
              lg: _.get(this.globalStyles, ['headline', 'font', 'lg'], 'Roboto'),
              md: _.get(this.globalStyles, ['headline', 'font', 'md'], 'Roboto'),
              sm: _.get(this.globalStyles, ['headline', 'font', 'sm'], 'Roboto')
            },
            fontWeight: {
              lg: _.get(this.globalStyles, ['headline', 'fontWeight', 'lg'], 600),
              md: _.get(this.globalStyles, ['headline', 'fontWeight', 'md'], 600),
              sm: _.get(this.globalStyles, ['headline', 'fontWeight', 'sm'], 600)
            },
            fontSize: {
              lg: _.get(this.globalStyles, ['headline', 'fontSize', 'lg'], '48px'),
              md: _.get(this.globalStyles, ['headline', 'fontSize', 'md'], '48px'),
              sm: _.get(this.globalStyles, ['headline', 'fontSize', 'sm'], '34px')
            },
            color: {
              lg: _.get(this.globalStyles, ['headline', 'color', 'lg'], '#0C226C'),
              md: _.get(this.globalStyles, ['headline', 'color', 'md'], '#0C226C'),
              sm: _.get(this.globalStyles, ['headline', 'color', 'sm'], '#0C226C')
            },
            align: {
              lg: _.get(this.globalStyles, ['headline', 'align', 'lg'], 'center'),
              md: _.get(this.globalStyles, ['headline', 'align', 'md'], 'center'),
              sm: _.get(this.globalStyles, ['headline', 'align', 'sm'], 'center')
            },
            textDecoration: {
              lg: _.get(this.globalStyles, ['headline', 'underline', 'lg'], '') === true ? 'underline' : '',
              md: _.get(this.globalStyles, ['headline', 'underline', 'md'], '') === true ? 'underline' : '',
              sm: _.get(this.globalStyles, ['headline', 'underline', 'sm'], '') === true ? 'underline' : ''
            },
            textStyle: {
              lg: _.get(this.globalStyles, ['headline', 'italic', 'lg'], '') === true ? 'italic' : '',
              md: _.get(this.globalStyles, ['headline', 'italic', 'md'], '') === true ? 'italic' : '',
              sm: _.get(this.globalStyles, ['headline', 'italic', 'sm'], '') === true ? 'italic' : ''
            },
            lineHeight: {
              lg: 1.3,
              md: 1.3,
              sm: 1.3
            },
            letterSpacing: {
              lg: 0,
              md: 0,
              sm: 0
            }
          },
          'h2': {
            fontFamily: {
              lg: _.get(this.globalStyles, ['headline', 'font', 'lg'], 'Roboto'),
              md: _.get(this.globalStyles, ['headline', 'font', 'md'], 'Roboto'),
              sm: _.get(this.globalStyles, ['headline', 'font', 'sm'], 'Roboto')
            },
            fontWeight: {
              lg: _.get(this.globalStyles, ['headline', 'fontWeight', 'lg'], 600),
              md: _.get(this.globalStyles, ['headline', 'fontWeight', 'md'], 600),
              sm: _.get(this.globalStyles, ['headline', 'fontWeight', 'sm'], 600)
            },
            fontSize: {
              lg: _.get(this.globalStyles, ['headline', 'fontSize', 'lg'], '48px'),
              md: _.get(this.globalStyles, ['headline', 'fontSize', 'md'], '48px'),
              sm: _.get(this.globalStyles, ['headline', 'fontSize', 'sm'], '34px')
            },
            color: {
              lg: _.get(this.globalStyles, ['headline', 'color', 'lg'], '#0C226C'),
              md: _.get(this.globalStyles, ['headline', 'color', 'md'], '#0C226C'),
              sm: _.get(this.globalStyles, ['headline', 'color', 'sm'], '#0C226C')
            },
            align: {
              lg: _.get(this.globalStyles, ['headline', 'align', 'lg'], 'center'),
              md: _.get(this.globalStyles, ['headline', 'align', 'md'], 'center'),
              sm: _.get(this.globalStyles, ['headline', 'align', 'sm'], 'center')
            },
            textDecoration: {
              lg: _.get(this.globalStyles, ['headline', 'underline', 'lg'], '') === true ? 'underline' : '',
              md: _.get(this.globalStyles, ['headline', 'underline', 'md'], '') === true ? 'underline' : '',
              sm: _.get(this.globalStyles, ['headline', 'underline', 'sm'], '') === true ? 'underline' : ''
            },
            textStyle: {
              lg: _.get(this.globalStyles, ['headline', 'italic', 'lg'], '') === true ? 'italic' : '',
              md: _.get(this.globalStyles, ['headline', 'italic', 'md'], '') === true ? 'italic' : '',
              sm: _.get(this.globalStyles, ['headline', 'italic', 'sm'], '') === true ? 'italic' : ''
            },
            lineHeight: {
              lg: 1.3,
              md: 1.3,
              sm: 1.3
            },
            letterSpacing: {
              lg: 0,
              md: 0,
              sm: 0
            }
          },
          'h3': {
            fontFamily: {
              lg: _.get(this.globalStyles, ['headline', 'font', 'lg'], 'Roboto'),
              md: _.get(this.globalStyles, ['headline', 'font', 'md'], 'Roboto'),
              sm: _.get(this.globalStyles, ['headline', 'font', 'sm'], 'Roboto')
            },
            fontWeight: {
              lg: _.get(this.globalStyles, ['headline', 'fontWeight', 'lg'], 600),
              md: _.get(this.globalStyles, ['headline', 'fontWeight', 'md'], 600),
              sm: _.get(this.globalStyles, ['headline', 'fontWeight', 'sm'], 600)
            },
            fontSize: {
              lg: _.get(this.globalStyles, ['headline', 'fontSize', 'lg'], '48px'),
              md: _.get(this.globalStyles, ['headline', 'fontSize', 'md'], '48px'),
              sm: _.get(this.globalStyles, ['headline', 'fontSize', 'sm'], '34px')
            },
            color: {
              lg: _.get(this.globalStyles, ['headline', 'color', 'lg'], '#0C226C'),
              md: _.get(this.globalStyles, ['headline', 'color', 'md'], '#0C226C'),
              sm: _.get(this.globalStyles, ['headline', 'color', 'sm'], '#0C226C')
            },
            align: {
              lg: _.get(this.globalStyles, ['headline', 'align', 'lg'], 'center'),
              md: _.get(this.globalStyles, ['headline', 'align', 'md'], 'center'),
              sm: _.get(this.globalStyles, ['headline', 'align', 'sm'], 'center')
            },
            textDecoration: {
              lg: _.get(this.globalStyles, ['headline', 'underline', 'lg'], '') === true ? 'underline' : '',
              md: _.get(this.globalStyles, ['headline', 'underline', 'md'], '') === true ? 'underline' : '',
              sm: _.get(this.globalStyles, ['headline', 'underline', 'sm'], '') === true ? 'underline' : ''
            },
            textStyle: {
              lg: _.get(this.globalStyles, ['headline', 'italic', 'lg'], '') === true ? 'italic' : '',
              md: _.get(this.globalStyles, ['headline', 'italic', 'md'], '') === true ? 'italic' : '',
              sm: _.get(this.globalStyles, ['headline', 'italic', 'sm'], '') === true ? 'italic' : ''
            },
            lineHeight: {
              lg: 1.3,
              md: 1.3,
              sm: 1.3
            },
            letterSpacing: {
              lg: 0,
              md: 0,
              sm: 0
            }
          },
          'h4': {
            fontFamily: {
              lg: _.get(this.globalStyles, ['headline', 'font', 'lg'], 'Roboto'),
              md: _.get(this.globalStyles, ['headline', 'font', 'md'], 'Roboto'),
              sm: _.get(this.globalStyles, ['headline', 'font', 'sm'], 'Roboto')
            },
            fontWeight: {
              lg: _.get(this.globalStyles, ['headline', 'fontWeight', 'lg'], 600),
              md: _.get(this.globalStyles, ['headline', 'fontWeight', 'md'], 600),
              sm: _.get(this.globalStyles, ['headline', 'fontWeight', 'sm'], 600)
            },
            fontSize: {
              lg: _.get(this.globalStyles, ['headline', 'fontSize', 'lg'], '48px'),
              md: _.get(this.globalStyles, ['headline', 'fontSize', 'md'], '48px'),
              sm: _.get(this.globalStyles, ['headline', 'fontSize', 'sm'], '34px')
            },
            color: {
              lg: _.get(this.globalStyles, ['headline', 'color', 'lg'], '#0C226C'),
              md: _.get(this.globalStyles, ['headline', 'color', 'md'], '#0C226C'),
              sm: _.get(this.globalStyles, ['headline', 'color', 'sm'], '#0C226C')
            },
            align: {
              lg: _.get(this.globalStyles, ['headline', 'align', 'lg'], 'center'),
              md: _.get(this.globalStyles, ['headline', 'align', 'md'], 'center'),
              sm: _.get(this.globalStyles, ['headline', 'align', 'sm'], 'center')
            },
            textDecoration: {
              lg: _.get(this.globalStyles, ['headline', 'underline', 'lg'], '') === true ? 'underline' : '',
              md: _.get(this.globalStyles, ['headline', 'underline', 'md'], '') === true ? 'underline' : '',
              sm: _.get(this.globalStyles, ['headline', 'underline', 'sm'], '') === true ? 'underline' : ''
            },
            textStyle: {
              lg: _.get(this.globalStyles, ['headline', 'italic', 'lg'], '') === true ? 'italic' : '',
              md: _.get(this.globalStyles, ['headline', 'italic', 'md'], '') === true ? 'italic' : '',
              sm: _.get(this.globalStyles, ['headline', 'italic', 'sm'], '') === true ? 'italic' : ''
            },
            lineHeight: {
              lg: 1.3,
              md: 1.3,
              sm: 1.3
            },
            letterSpacing: {
              lg: 0,
              md: 0,
              sm: 0
            }
          },
          'h5': {
            fontFamily: {
              lg: 'ProximaNova',
              md: 'ProximaNova',
              sm: 'ProximaNova'
            },
            fontWeight: {
              lg: 500,
              md: 500,
              sm: 500
            },
            fontSize: {
              lg: '20px',
              md: '20px',
              sm: '20px'
            },
            color: {
              lg: '#212529',
              md: '#212529',
              sm: '#212529'
            },
            align: {
              lg: 'left',
              md: 'left',
              sm: 'left'
            },
            textDecoration: {
              lg: _.get(this.globalStyles, ['headline', 'underline', 'lg'], '') === true ? 'underline' : '',
              md: _.get(this.globalStyles, ['headline', 'underline', 'md'], '') === true ? 'underline' : '',
              sm: _.get(this.globalStyles, ['headline', 'underline', 'sm'], '') === true ? 'underline' : ''
            },
            textStyle: {
              lg: _.get(this.globalStyles, ['headline', 'italic', 'lg'], '') === true ? 'italic' : '',
              md: _.get(this.globalStyles, ['headline', 'italic', 'md'], '') === true ? 'italic' : '',
              sm: _.get(this.globalStyles, ['headline', 'italic', 'sm'], '') === true ? 'italic' : ''
            },
            lineHeight: {
              lg: 1.2,
              md: 1.2,
              sm: 1.2
            },
            letterSpacing: {
              lg: 0,
              md: 0,
              sm: 0
            }
          },
          'h6': {
            fontFamily: {
              lg: 'ProximaNova',
              md: 'ProximaNova',
              sm: 'ProximaNova'
            },
            fontWeight: {
              lg: 500,
              md: 500,
              sm: 500
            },
            fontSize: {
              lg: '16px',
              md: '16px',
              sm: '16px'
            },
            color: {
              lg: '#212529',
              md: '#212529',
              sm: '#212529'
            },
            align: {
              lg: 'left',
              md: 'left',
              sm: 'left'
            },
            textDecoration: {
              lg: _.get(this.globalStyles, ['headline', 'underline', 'lg'], '') === true ? 'underline' : '',
              md: _.get(this.globalStyles, ['headline', 'underline', 'md'], '') === true ? 'underline' : '',
              sm: _.get(this.globalStyles, ['headline', 'underline', 'sm'], '') === true ? 'underline' : ''
            },
            textStyle: {
              lg: _.get(this.globalStyles, ['headline', 'italic', 'lg'], '') === true ? 'italic' : '',
              md: _.get(this.globalStyles, ['headline', 'italic', 'md'], '') === true ? 'italic' : '',
              sm: _.get(this.globalStyles, ['headline', 'italic', 'sm'], '') === true ? 'italic' : ''
            },
            lineHeight: {
              lg: 1.2,
              md: 1.2,
              sm: 1.2
            },
            letterSpacing: {
              lg: 0,
              md: 0,
              sm: 0
            }
          },
          'paragraph': {
            fontFamily: {
              lg: _.get(this.globalStyles, ['paragpraph', 'font', 'lg'], 'Roboto'),
              md: _.get(this.globalStyles, ['paragpraph', 'font', 'md'], 'Roboto'),
              sm: _.get(this.globalStyles, ['paragpraph', 'font', 'sm'], 'Roboto')
            },
            fontWeight: {
              lg: _.get(this.globalStyles, ['paragpraph', 'fontWeight', 'lg'], 400),
              md: _.get(this.globalStyles, ['paragpraph', 'fontWeight', 'md'], 400),
              sm: _.get(this.globalStyles, ['paragpraph', 'fontWeight', 'sm'], 400)
            },
            fontSize: {
              lg: _.get(this.globalStyles, ['paragpraph', 'fontSize', 'lg'], '18px'),
              md: _.get(this.globalStyles, ['paragpraph', 'fontSize', 'md'], '18px'),
              sm: _.get(this.globalStyles, ['paragpraph', 'fontSize', 'sm'], '18px')
            },
            color: {
              lg: _.get(this.globalStyles, ['paragpraph', 'color', 'lg'], '#383838'),
              md: _.get(this.globalStyles, ['paragpraph', 'color', 'md'], '#383838'),
              sm: _.get(this.globalStyles, ['paragpraph', 'color', 'sm'], '#383838')
            },
            align: {
              lg: _.get(this.globalStyles, ['paragpraph', 'align', 'lg'], 'center'),
              md: _.get(this.globalStyles, ['paragpraph', 'align', 'md'], 'center'),
              sm: _.get(this.globalStyles, ['paragpraph', 'align', 'sm'], 'center')
            },
            textDecoration: {
              lg: _.get(this.globalStyles, ['paragpraph', 'underline', 'lg'], '') === true ? 'underline' : '',
              md: _.get(this.globalStyles, ['paragpraph', 'underline', 'md'], '') === true ? 'underline' : '',
              sm: _.get(this.globalStyles, ['paragpraph', 'underline', 'sm'], '') === true ? 'underline' : ''
            },
            textStyle: {
              lg: _.get(this.globalStyles, ['paragpraph', 'italic', 'lg'], '') === true ? 'italic' : '',
              md: _.get(this.globalStyles, ['paragpraph', 'italic', 'md'], '') === true ? 'italic' : '',
              sm: _.get(this.globalStyles, ['paragpraph', 'italic', 'sm'], '') === true ? 'italic' : ''
            },
            lineHeight: {
              lg: 1.3,
              md: 1.3,
              sm: 1.3
            },
            letterSpacing: {
              lg: 0,
              md: 0,
              sm: 0
            }
          },
          'link': {
            fontFamily: {
              lg: _.get(this.globalStyles, ['link', 'font', 'lg'], 'Roboto'),
              md: _.get(this.globalStyles, ['link', 'font', 'md'], 'Roboto'),
              sm: _.get(this.globalStyles, ['link', 'font', 'sm'], 'Roboto')
            },
            fontWeight: {
              lg: _.get(this.globalStyles, ['link', 'fontWeight', 'lg'], 400),
              md: _.get(this.globalStyles, ['link', 'fontWeight', 'md'], 400),
              sm: _.get(this.globalStyles, ['link', 'fontWeight', 'sm'], 400)
            },
            fontSize: {
              lg: _.get(this.globalStyles, ['link', 'fontSize', 'lg'], '18px'),
              md: _.get(this.globalStyles, ['link', 'fontSize', 'md'], '18px'),
              sm: _.get(this.globalStyles, ['link', 'fontSize', 'sm'], '18px')
            },
            color: {
              lg: _.get(this.globalStyles, ['link', 'color', 'lg'], '#7CACFA'),
              md: _.get(this.globalStyles, ['link', 'color', 'md'], '#7CACFA'),
              sm: _.get(this.globalStyles, ['link', 'color', 'sm'], '#7CACFA')
            },
            align: {
              lg: _.get(this.globalStyles, ['link', 'align', 'lg'], 'center'),
              md: _.get(this.globalStyles, ['link', 'align', 'md'], 'center'),
              sm: _.get(this.globalStyles, ['link', 'align', 'sm'], 'center')
            },
            textDecoration: {
              lg: _.get(this.globalStyles, ['link', 'underline', 'lg'], '') === true ? 'underline' : '',
              md: _.get(this.globalStyles, ['link', 'underline', 'md'], '') === true ? 'underline' : '',
              sm: _.get(this.globalStyles, ['link', 'underline', 'sm'], '') === true ? 'underline' : ''
            },
            textStyle: {
              lg: _.get(this.globalStyles, ['paragpraph', 'italic', 'lg'], '') === true ? 'italic' : '',
              md: _.get(this.globalStyles, ['paragpraph', 'italic', 'md'], '') === true ? 'italic' : '',
              sm: _.get(this.globalStyles, ['paragpraph', 'italic', 'sm'], '') === true ? 'italic' : ''
            },
            lineHeight: {
              lg: 1.3,
              md: 1.3,
              sm: 1.3
            },
            letterSpacing: {
              lg: 0,
              md: 0,
              sm: 0
            }
          },
          'link-hover': {
            fontFamily: {
              lg: _.get(this.globalStyles, ['link', 'font', 'lg'], 'Roboto'),
              md: _.get(this.globalStyles, ['link', 'font', 'md'], 'Roboto'),
              sm: _.get(this.globalStyles, ['link', 'font', 'sm'], 'Roboto')
            },
            fontWeight: {
              lg: _.get(this.globalStyles, ['link', 'fontWeight', 'lg'], 400),
              md: _.get(this.globalStyles, ['link', 'fontWeight', 'md'], 400),
              sm: _.get(this.globalStyles, ['link', 'fontWeight', 'sm'], 400)
            },
            fontSize: {
              lg: _.get(this.globalStyles, ['link', 'fontSize', 'lg'], '18px'),
              md: _.get(this.globalStyles, ['link', 'fontSize', 'md'], '18px'),
              sm: _.get(this.globalStyles, ['link', 'fontSize', 'sm'], '18px')
            },
            color: {
              lg: _.get(this.globalStyles, ['link', 'color', 'lg'], '#5f8edc'),
              md: _.get(this.globalStyles, ['link', 'color', 'md'], '#5f8edc'),
              sm: _.get(this.globalStyles, ['link', 'color', 'sm'], '#5f8edc')
            },
            align: {
              lg: _.get(this.globalStyles, ['link', 'align', 'lg'], 'center'),
              md: _.get(this.globalStyles, ['link', 'align', 'md'], 'center'),
              sm: _.get(this.globalStyles, ['link', 'align', 'sm'], 'center')
            },
            textDecoration: {
              lg: _.get(this.globalStyles, ['link', 'underline', 'lg'], '') === true ? 'underline' : '',
              md: _.get(this.globalStyles, ['link', 'underline', 'md'], '') === true ? 'underline' : '',
              sm: _.get(this.globalStyles, ['link', 'underline', 'sm'], '') === true ? 'underline' : ''
            },
            textStyle: {
              lg: _.get(this.globalStyles, ['paragpraph', 'italic', 'lg'], '') === true ? 'italic' : '',
              md: _.get(this.globalStyles, ['paragpraph', 'italic', 'md'], '') === true ? 'italic' : '',
              sm: _.get(this.globalStyles, ['paragpraph', 'italic', 'sm'], '') === true ? 'italic' : ''
            },
            lineHeight: {
              lg: 1.3,
              md: 1.3,
              sm: 1.3
            },
            letterSpacing: {
              lg: 0,
              md: 0,
              sm: 0
            }
          }
        })
      }

      if (!this.globalStyles.textStyles.h4) {
        this.$set(this.globalStyles.textStyles, 'h4', {
          fontFamily: {
            lg: _.get(this.globalStyles, ['headline', 'font', 'lg'], 'Roboto'),
            md: _.get(this.globalStyles, ['headline', 'font', 'md'], 'Roboto'),
            sm: _.get(this.globalStyles, ['headline', 'font', 'sm'], 'Roboto')
          },
          fontWeight: {
            lg: _.get(this.globalStyles, ['headline', 'fontWeight', 'lg'], 400),
            md: _.get(this.globalStyles, ['headline', 'fontWeight', 'md'], 400),
            sm: _.get(this.globalStyles, ['headline', 'fontWeight', 'sm'], 400)
          },
          fontSize: {
            lg: _.get(this.globalStyles, ['headline', 'fontSize', 'lg'], '48px'),
            md: _.get(this.globalStyles, ['headline', 'fontSize', 'md'], '48px'),
            sm: _.get(this.globalStyles, ['headline', 'fontSize', 'sm'], '34px')
          },
          color: {
            lg: _.get(this.globalStyles, ['headline', 'color', 'lg'], '#0C226C'),
            md: _.get(this.globalStyles, ['headline', 'color', 'md'], '#0C226C'),
            sm: _.get(this.globalStyles, ['headline', 'color', 'sm'], '#0C226C')
          },
          align: {
            lg: _.get(this.globalStyles, ['headline', 'align', 'lg'], 'center'),
            md: _.get(this.globalStyles, ['headline', 'align', 'md'], 'center'),
            sm: _.get(this.globalStyles, ['headline', 'align', 'sm'], 'center')
          },
          textDecoration: {
            lg: _.get(this.globalStyles, ['headline', 'underline', 'lg'], '') === true ? 'underline' : '',
            md: _.get(this.globalStyles, ['headline', 'underline', 'md'], '') === true ? 'underline' : '',
            sm: _.get(this.globalStyles, ['headline', 'underline', 'sm'], '') === true ? 'underline' : ''
          },
          textStyle: {
            lg: _.get(this.globalStyles, ['headline', 'italic', 'lg'], '') === true ? 'italic' : '',
            md: _.get(this.globalStyles, ['headline', 'italic', 'md'], '') === true ? 'italic' : '',
            sm: _.get(this.globalStyles, ['headline', 'italic', 'sm'], '') === true ? 'italic' : ''
          },
          lineHeight: {
            lg: 1.3,
            md: 1.3,
            sm: 1.3
          },
          letterSpacing: {
            lg: 0,
            md: 0,
            sm: 0
          }
        })
      }
      
      if (!this.globalStyles.textStyles.h5) {
        this.$set(this.globalStyles.textStyles, 'h5', {
          fontFamily: {
            lg: 'ProximaNova',
            md: 'ProximaNova',
            sm: 'ProximaNova'
          },
          fontWeight: {
            lg: 500,
            md: 500,
            sm: 500
          },
          fontSize: {
            lg: '20px',
            md: '20px',
            sm: '20px'
          },
          color: {
            lg: '#212529',
            md: '#212529',
            sm: '#212529'
          },
          align: {
            lg: 'left',
            md: 'left',
            sm: 'left'
          },
          textDecoration: {
            lg: _.get(this.globalStyles, ['headline', 'underline', 'lg'], '') === true ? 'underline' : '',
            md: _.get(this.globalStyles, ['headline', 'underline', 'md'], '') === true ? 'underline' : '',
            sm: _.get(this.globalStyles, ['headline', 'underline', 'sm'], '') === true ? 'underline' : ''
          },
          textStyle: {
            lg: _.get(this.globalStyles, ['headline', 'italic', 'lg'], '') === true ? 'italic' : '',
            md: _.get(this.globalStyles, ['headline', 'italic', 'md'], '') === true ? 'italic' : '',
            sm: _.get(this.globalStyles, ['headline', 'italic', 'sm'], '') === true ? 'italic' : ''
          },
          lineHeight: {
            lg: 1.2,
            md: 1.2,
            sm: 1.2
          },
          letterSpacing: {
            lg: 0,
            md: 0,
            sm: 0
          }
        })
      }

      if (!this.globalStyles.textStyles.h6) {
        this.$set(this.globalStyles.textStyles, 'h6', {
          fontFamily: {
            lg: 'ProximaNova',
            md: 'ProximaNova',
            sm: 'ProximaNova'
          },
          fontWeight: {
            lg: 500,
            md: 500,
            sm: 500
          },
          fontSize: {
            lg: '16px',
            md: '16px',
            sm: '16px'
          },
          color: {
            lg: '#212529',
            md: '#212529',
            sm: '#212529'
          },
          align: {
            lg: 'left',
            md: 'left',
            sm: 'left'
          },
          textDecoration: {
            lg: _.get(this.globalStyles, ['headline', 'underline', 'lg'], '') === true ? 'underline' : '',
            md: _.get(this.globalStyles, ['headline', 'underline', 'md'], '') === true ? 'underline' : '',
            sm: _.get(this.globalStyles, ['headline', 'underline', 'sm'], '') === true ? 'underline' : ''
          },
          textStyle: {
            lg: _.get(this.globalStyles, ['headline', 'italic', 'lg'], '') === true ? 'italic' : '',
            md: _.get(this.globalStyles, ['headline', 'italic', 'md'], '') === true ? 'italic' : '',
            sm: _.get(this.globalStyles, ['headline', 'italic', 'sm'], '') === true ? 'italic' : ''
          },
          lineHeight: {
            lg: 1.2,
            md: 1.2,
            sm: 1.2
          },
          letterSpacing: {
            lg: 0,
            md: 0,
            sm: 0
          }
        })
      }

      return this.globalStyles.textStyles[this.type]
    }
  },

  methods: {
    setValue (key, val) {
      if (this.resolutioner === 'lg') {
        const lg = this.textStyles[key].lg
        const md = this.textStyles[key].md
        const sm = this.textStyles[key].sm

        const obj = {
          lg: val,
          md: md === lg ? val : md,
          sm: sm === lg ? val : sm
        }

        return this.$set(this.textStyles, key, obj)
      }

      this.textStyles[key][this.resolutioner] = val
    },
    fontSizes () {
      const arr = Array(100)
        .fill({})
        .map((_, i) => `${i + 1}px`)
      return arr.slice(10)
    }
  }
}
</script>
